import React, { useEffect, useState } from 'react';
import { ActivityIndicator, Modal, Platform, View } from 'react-native';
import styled from 'styled-components/native';
import Colors from '../../constants/Colors';
import { B, H5, Subtitle } from '../Typography';
import SelectField, { SelectOption } from './SelectField';
import FlatListPicker from '../FlatlistPicker';
import { getHoursLabel } from '../charts/LineChart';

const FormHoursView = styled.View`
  width: 100%;
`;

const StyledContainer = styled.View`
  flex-direction: row;
  width: 100%;
  padding-right: 4px;
  height: 56px;
  margin-bottom: 12px;
`;

const StyledTimeInputView = styled.TouchableOpacity<{
  hasHelperText?: boolean;
  error?: boolean;
}>`
  min-height: auto;
  background-color: #fff;
  flex-direction: row;
  border: 1px solid ${p => (p.error ? Colors.error : Colors.formInputBorder)};
  border-radius: 8px;
  padding: 16px;
  width: 100%;
  height: 56px;
  margin-bottom: ${p => (p.error || p.hasHelperText ? 12 : 12)}px;
`;

const StyledModalView = styled.View`
  width: 100%;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const StyledInputText = styled(B)<{
  hasValue?: boolean;
  textAlign: 'left' | 'right';
}>`
  font-family: ${p => (p.hasValue ? 'DMSans-Bold' : 'WorkSans-Regular')};
  font-size: 18px;
  line-height: 23px;
  opacity: ${p => (p.hasValue ? 1 : 0.3)};
  flex: 1;
  text-align: ${p => p.textAlign};
`;

const StyledSubtitle = styled(Subtitle)<{ removeRightPadding?: boolean }>`
  margin: ${p => (p.removeRightPadding ? '0px 0px 0px 8px' : '0px 8px')};
`;

const StyledModalBackgroundContainer = styled.TouchableWithoutFeedback`
  width: 100%;
  height: 100%;
  position: absolute;
`;

const StyledModalBackground = styled.View`
  width: 100%;
  height: 100%;
  position: absolute;
  background-color: black;
  opacity: 0.3;
`;

const StyledTimePickerView = styled.View`
  flex-direction: row;
  align-items: center;
  justify-content: center;
  padding: ${Platform.OS === 'web' ? '14px' : '0'};
`;

const StyledPickerView = styled.View`
  background-color: white;
  width: 100%;
  padding: ${Platform.OS === 'web' ? '24px 10px 10px 10px' : '10px'};
  max-width: ${Platform.OS === 'web' ? '308px' : '280px'};
  border-radius: 8px;
`;

const StyledModalButtonView = styled.View`
  flex-direction: row;
  width: 100%;
  align-items: center;
  justify-content: flex-end;
  margin-top: 32px;
  padding-bottom: 4px;
`;

const StyledModalButton = styled.TouchableOpacity`
  height: 20px;
  margin-right: 16px;
`;

export interface HoursFieldProps {
  label?: string;
  value?: number;
  isLoading?: boolean;
  textAlign?: 'left' | 'right';
  onChange?: (result: number) => void;
}

const HoursField: React.FC<HoursFieldProps> = ({
  label,
  value,
  isLoading,
  textAlign = 'right',
  onChange
}) => {
  const [showPicker, setShowPicker] = useState(false);
  const [input, setInput] = useState<{ hour?: string; min?: string }>({
    hour: '0',
    min: '0'
  });
  const [currentValue, setCurrentValue] = useState<number | undefined>(
    typeof value === 'number' ? value : undefined
  );

  const getTimeString = (value?: number) => {
    const result = { hour: '0', min: '0' };
    if (value) {
      const time = value.toString().split('.');
      result.hour = Number(time[0]).toString();
      result.min = (
        time.length === 2 ? Math.round(60 * Number('.' + time[1])) : 0
      ).toString();
      return result;
    }
    return result;
  };

  useEffect(() => {
    if (value && !isNaN(value)) {
      setInput(getTimeString(value));
      setCurrentValue(value);
    } else {
      setInput({ hour: '0', min: '0' });
      setCurrentValue(undefined);
    }
  }, [value]);

  const getCountOptions = (count: number) => {
    const arr = [];
    for (let i = 0; i <= count; i++) {
      arr.push(i.toString());
    }
    return arr;
  };

  return (
    <View>
      <FormHoursView>
        <StyledContainer>
          <StyledTimeInputView
            disabled={isLoading}
            onPress={() => {
              setShowPicker(true);
            }}
          >
            <H5 color='#615E6D'>{label}</H5>
            {isLoading ? (
              <ActivityIndicator color={Colors.primary} />
            ) : (
              <StyledInputText
                textAlign={textAlign}
                color='dark'
                hasValue={Boolean(currentValue)}
              >
                {currentValue ? getHoursLabel(currentValue) : ''}
              </StyledInputText>
            )}
          </StyledTimeInputView>
        </StyledContainer>
      </FormHoursView>
      <Modal visible={showPicker} transparent={true} animationType='fade'>
        <StyledModalView>
          <StyledModalBackgroundContainer
            onPress={() => {
              setShowPicker(false);
            }}
          >
            <StyledModalBackground />
          </StyledModalBackgroundContainer>
          <StyledPickerView>
            <StyledTimePickerView>
              {Platform.OS === 'web' ? (
                <SelectField
                  value={input?.hour}
                  placeholder='hr'
                  options={getCountOptions(23)}
                  onChange={option => {
                    setInput({
                      ...input,
                      hour: String((option as SelectOption).value)
                    });
                  }}
                />
              ) : (
                <FlatListPicker
                  onValueChanged={data => {
                    setInput({ ...input, hour: data });
                  }}
                  initialValue={input?.hour}
                  options={getCountOptions(23)}
                />
              )}
              <StyledSubtitle color='dark'>hr</StyledSubtitle>
              {Platform.OS === 'web' ? (
                <SelectField
                  value={input?.min}
                  placeholder='Minutes'
                  options={getCountOptions(59)}
                  onChange={option => {
                    setInput({
                      ...input,
                      min: String((option as SelectOption).value)
                    });
                  }}
                />
              ) : (
                <FlatListPicker
                  onValueChanged={data => {
                    setInput({ ...input, min: data });
                  }}
                  initialValue={input?.min}
                  options={getCountOptions(59)}
                />
              )}
              <StyledSubtitle removeRightPadding color='dark'>
                min
              </StyledSubtitle>
            </StyledTimePickerView>
            <StyledModalButtonView>
              <StyledModalButton
                onPress={() => {
                  setShowPicker(false);
                }}
              >
                <B color='dark'>Cancel</B>
              </StyledModalButton>
              <StyledModalButton
                onPress={() => {
                  setShowPicker(false);
                  setCurrentValue(
                    Number(input?.hour) + Number(input?.min) / 60
                  );
                  onChange?.(Number(input?.hour) + Number(input?.min) / 60);
                }}
              >
                <B>Confirm</B>
              </StyledModalButton>
            </StyledModalButtonView>
          </StyledPickerView>
        </StyledModalView>
      </Modal>
    </View>
  );
};

export default HoursField;
